import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { ReactNode } from "react";
import { useMemo } from "react";

import { DescriptionContainer } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/components/DescriptionContainer";
import { InlineOfferActions } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/InlineOfferActions";
import { useOfferPageContext } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/OfferPageProvider";
import type { CheckoutContextProps } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { APPEARANCE } from "@/react/providers/Theme";

import { PaywallCustomization } from "..";
import { ScopedThemeProvider } from "../../Offer/ScopedThemeProvider";
import { PriceSelectorLine } from "../../PriceSelectorModal/PriceSelectorLine";
import { getBestFontColor } from "../PriceCallout/getBestFontColor";

export interface OfferContent {
  backgroundColor: string;
  cover: ReactNode;
  header: ReactNode;
  id?: string;
  shouldShowCountdown: boolean;
}

export interface OfferTemplateContent {
  backgroundColor: string;
  cover: ReactNode;
  header: ReactNode;
  shouldShowCountdown: boolean;
}

type CommonData = "paywall";
type PriceSelectorData =
  | "previewMutation"
  | "setSelectedPrice"
  | "isSelectedPrice"
  | "getSelectedPrice"
  | "couponPreviewMutation";

interface OfferTemplateProps {
  className?: string;
  content: OfferTemplateContent;
  errorAlert?: ReactNode;
  shouldShowActions?: boolean;
  useHooks: () => Pick<CheckoutContextProps, CommonData | PriceSelectorData>;
}

export const OfferTemplate = ({
  className,
  content,
  errorAlert,
  shouldShowActions = true,
  useHooks,
}: OfferTemplateProps) => {
  const { isPreviewing } = useOfferPageContext();

  const descriptionPageAppearance = useMemo(() => {
    const fontColor = getBestFontColor(content.backgroundColor);
    if (fontColor === "#FFFFFF") {
      return APPEARANCE.DARK;
    }
    return APPEARANCE.LIGHT;
  }, [content.backgroundColor]);

  return (
    <ScopedThemeProvider appearance={descriptionPageAppearance}>
      <DescriptionContainer
        className={classNames(
          "size-full px-5 py-6 md:px-10 md:py-12",
          className,
        )}
        style={{ backgroundColor: content.backgroundColor }}
      >
        {errorAlert}
        {content.shouldShowCountdown && (
          <PaywallCustomization.Countdown variant="offer_v2" />
        )}
        {content.header}
        {content.cover}
        {shouldShowActions && (
          <div className="flex flex-col gap-y-5 w-full md:w-[540px] items-center">
            <div className="w-full">
              <PriceSelectorLine
                useHooks={useHooks}
                isPreviewing={isPreviewing}
              />
            </div>
            <InlineOfferActions />
          </div>
        )}
      </DescriptionContainer>
    </ScopedThemeProvider>
  );
};
